import { useLoaderData, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Link as MuiLink,
} from '@mui/material'
import PageAppBar from 'components/PageAppBar'
import { ResourceModel } from 'types'
import { base_url } from 'env'
import {
  CheckCircleOutline,
  HighlightOff,
  CloudUpload,
} from '@mui/icons-material'

export default function ResourcePreviewPage() {
  const resource = useLoaderData() as ResourceModel
  const navigate = useNavigate()
  const token = localStorage.getItem('token')

  // --- CORRECTION 1 : Harmoniser la structure de données ---
  // On ajoute query:'' à tous les objets et on retire le '?' pour une construction dynamique.
  const fileTypes = [
    { key: 'hasVideo', label: 'Vidéo', type: 'video', query: '' },
    {
      key: 'hasMobilePdf',
      label: 'PDF Mobile',
      type: 'pdf',
      query: 'mobile=1',
    },
    { key: 'hasPaperPdf', label: 'PDF Papier', type: 'pdf', query: '' },
    {
      key: 'hasMobilePng',
      label: 'Image Mobile',
      type: 'png',
      query: 'mobile=1',
    },
    { key: 'hasPaperPng', label: 'Image Papier', type: 'png', query: '' },
  ] as const

  // --- CORRECTION 2 : Fonction de construction d'URL robuste ---
  const buildFileUrl = (
    file: (typeof fileTypes)[number]
  ): string | undefined => {
    if (!resource[file.key]) {
      return undefined
    }

    const params = new URLSearchParams()
    if (file.query) {
      // Pour des queries simples comme 'mobile=1'
      const [key, value] = file.query.split('=')
      params.append(key, value)
    }
    if (token) {
      params.append('jwt', token)
    }

    const queryString = params.toString()
    return `${base_url}/resource/${resource.id}/${file.type}${queryString ? `?${queryString}` : ''}`
  }

  return (
    <PageAppBar pageName={`Ressource: ${resource.title}`}>
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {resource.title}
          </Typography>
          <Chip
            label={`Type: ${resource.type}`}
            color={resource.type === 'local' ? 'primary' : 'secondary'}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate(`/resource/${resource.id}/edit`)}
          >
            Modifier le titre/type
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<CloudUpload />}
            onClick={() => navigate(`/resource/${resource.id}/upload`)}
          >
            Gérer les fichiers
          </Button>
        </Box>

        <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Fichiers Disponibles
            </Typography>
            <Grid container spacing={2}>
              {fileTypes.map((file) => {
                const url = buildFileUrl(file)
                return (
                  <Grid item xs={12} sm={6} key={file.key}>
                    <MuiLink
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline={url ? 'hover' : 'none'}
                      sx={{
                        cursor: url ? 'pointer' : 'default',
                        display: 'block',
                      }}
                    >
                      <Chip
                        icon={
                          resource[file.key] ? (
                            <CheckCircleOutline />
                          ) : (
                            <HighlightOff />
                          )
                        }
                        label={file.label}
                        color={resource[file.key] ? 'success' : 'default'}
                        variant="outlined"
                        sx={{
                          width: '100%',
                          justifyContent: 'flex-start',
                          p: 2,
                          cursor: 'inherit',
                        }}
                        clickable={!!url}
                      />
                    </MuiLink>
                  </Grid>
                )
              })}
            </Grid>
          </CardContent>
        </Card>

        {resource.hasVideo && (
          <Card sx={{ width: '100%', maxWidth: '800px' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Aperçu Vidéo
              </Typography>
              <video
                controls
                width="100%"
                // On réutilise notre fonction pour la cohérence
                src={buildFileUrl(fileTypes.find((f) => f.key === 'hasVideo')!)}
              >
                Votre navigateur ne supporte pas la balise vidéo.
              </video>
            </CardContent>
          </Card>
        )}
      </Box>
    </PageAppBar>
  )
}
